import { dungApiThat, goiApi } from '../api/http';
import { mayChuBaoCao } from '../../mock/bao-cao-doanh-nghiep';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  CreateFolderInput,
  CreateSourceInput,
  DocumentFields,
  DocumentItem,
  DocumentOpen,
  DocumentPage,
  Folder,
  ModuleId,
  ModulePayload,
  QueryDocuments,
  Source,
  UpdateFolderInput,
  UpdateModulePayload,
  UpdateSourceInput,
} from '../../modules/bao-cao-doanh-nghiep/types';

/**
 * Module `bao-cao` sau Gateway (ADR-0081, `modules/bao-cao/README.md`). Định dạng dây giữ của erp-service
 * native: camelCase, `{items, total, nextCursor}`, thư mục và nguồn là mảng trần, lỗi Problem Details
 * (`lib/api/http.ts` đọc `detail`, `error_code`). Không có `organizationId`: tenant lấy từ token.
 */
const GOC = '/api/v1/m/bao-cao';

const ma = (id: string) => encodeURIComponent(id);

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi nghiệp vụ tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/** `?source=` chỉ gửi khi có: vắng là nguồn mặc định, như app di động. */
const theoNguon = (source: string | undefined) => (source === undefined ? {} : { source });

/**
 * Multipart của `POST`/`PATCH /documents`: trường `file` cùng trường `data` là **chuỗi JSON** của các
 * trường còn lại (không có `url`, `type` — backend suy từ tệp). Không tự đặt `Content-Type`.
 */
function bieuMau(du: DocumentFields, tep: File): FormData {
  const fd = new FormData();
  const con: DocumentFields = { ...du };
  delete con.url;
  delete con.type;
  fd.append('data', JSON.stringify(con));
  fd.append('file', tep, tep.name);
  return fd;
}

function queryTaiLieu(q: QueryDocuments): Record<string, string | number | undefined> {
  return {
    limit: q.limit,
    cursor: q.cursor,
    category: q.category,
    q: q.q,
    source: q.source,
    folder: q.folder,
    ...(q.deep ? { deep: 'true' } : {}),
    ...(q.chuaPhanLoai ? { chuaPhanLoai: 'true' } : {}),
  };
}

const TRANG_RONG: DocumentPage = { items: [], total: 0, nextCursor: null };

/**
 * Truy cập dữ liệu module `bao-cao`. Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả
 * (`mock/bao-cao-doanh-nghiep.ts`). `?mock=filtered-empty` diễn ở đây: có bộ lọc thì trả rỗng.
 */
export const baoCaoDoanhNghiepRepository = {
  // ─── Phân hệ ───

  /** `404` khi phân hệ chưa có số liệu ở nguồn đó — màn coi là "chưa nhập", cho nhập mới. */
  phanHe: (id: ModuleId, source?: string): Promise<ModulePayload> =>
    dungApiThat()
      ? goiApi(`${GOC}/modules/${ma(id)}`, { query: theoNguon(source) })
      : giaLap(() => {
        // `?mock=empty`: phân hệ chưa có số liệu, như backend trả `404`.
        if (mockState() === 'empty') throw mayChuBaoCao.chuaNhap();
        return mayChuBaoCao.phanHe(id, source);
      }),

  /** Ghi đè trọn gói; khối vắng là xoá khối. Trả payload mới. */
  luuPhanHe: (id: ModuleId, than: UpdateModulePayload, source?: string): Promise<ModulePayload> =>
    dungApiThat()
      ? goiApi(`${GOC}/modules/${ma(id)}`, { method: 'PUT', query: theoNguon(source), body: than })
      : giaLap(() => mayChuBaoCao.luuPhanHe(id, than, source)),

  // ─── Tài liệu ───

  taiLieu: (q: QueryDocuments): Promise<DocumentPage> => {
    if (dungApiThat()) return goiApi(`${GOC}/documents`, { query: queryTaiLieu(q) });
    if (mockState() === 'filtered-empty' && (q.q || q.category || q.folder || q.chuaPhanLoai)) return mockRequest(TRANG_RONG);
    return mockRequest(undefined, TRANG_RONG).then((rong) => rong ?? mayChuBaoCao.taiLieu(q));
  },

  /** URL mở tài liệu: tệp tải lên là URL ký 300 giây — mở ngay, không lưu lại. */
  moTaiLieu: (id: string): Promise<DocumentOpen> =>
    dungApiThat() ? goiApi(`${GOC}/documents/${ma(id)}/open`) : giaLap(() => mayChuBaoCao.moTaiLieu(id)),

  /** Liên kết ngoài đi JSON; có tệp thì multipart. */
  taoTaiLieu: (du: DocumentFields, tep: File | null, source?: string): Promise<DocumentItem> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents`, {
        method: 'POST',
        query: theoNguon(source),
        ...(tep ? { bieuMau: bieuMau(du, tep) } : { body: du }),
      })
      : giaLap(() => mayChuBaoCao.taoTaiLieu(du, tep ? { name: tep.name, size: tep.size } : null, source)),

  /** JSON một phần; có tệp mới thì multipart để thay tệp. */
  suaTaiLieu: (id: string, du: DocumentFields, tep: File | null = null): Promise<DocumentItem> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents/${ma(id)}`, { method: 'PATCH', ...(tep ? { bieuMau: bieuMau(du, tep) } : { body: du }) })
      : giaLap(() => mayChuBaoCao.suaTaiLieu(id, du, tep ? { name: tep.name, size: tep.size } : null)),

  /** Gỡ khỏi app (`isPublished: false`), không xoá dòng, tệp giữ nguyên. */
  goTaiLieu: (id: string): Promise<{ id: string; isPublished: boolean }> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents/${ma(id)}`, { method: 'DELETE' })
      : giaLap(() => mayChuBaoCao.goTaiLieu(id)),

  // ─── Thư mục ───

  /** Danh sách phẳng, đã sắp theo `path`. */
  thuMuc: (source: string | undefined, kemSoLuong: boolean): Promise<Folder[]> =>
    dungApiThat()
      ? goiApi(`${GOC}/folders`, { query: { ...theoNguon(source), ...(kemSoLuong ? { kemSoLuong: 'true' } : {}) } })
      : mockRequest(undefined, [] as Folder[]).then((rong) => rong ?? mayChuBaoCao.thuMuc(source, kemSoLuong)),

  demChuaPhanLoai: (source: string | undefined): Promise<{ documentsTotal: number }> =>
    dungApiThat()
      ? goiApi(`${GOC}/folders/chua-phan-loai`, { query: theoNguon(source) })
      : mockRequest(undefined, { documentsTotal: 0 }).then((rong) => rong ?? mayChuBaoCao.demChuaPhanLoai(source)),

  taoThuMuc: (du: CreateFolderInput): Promise<Folder> =>
    dungApiThat() ? goiApi(`${GOC}/folders`, { method: 'POST', body: du }) : giaLap(() => mayChuBaoCao.taoThuMuc(du)),

  suaThuMuc: (id: string, du: UpdateFolderInput): Promise<Folder> =>
    dungApiThat()
      ? goiApi(`${GOC}/folders/${ma(id)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuBaoCao.suaThuMuc(id, du)),

  /** `parentId: null` là đưa ra gốc của nguồn. */
  chuyenThuMuc: (id: string, parentId: string | null): Promise<Folder> =>
    dungApiThat()
      ? goiApi(`${GOC}/folders/${ma(id)}/move`, { method: 'POST', body: { parentId } })
      : giaLap(() => mayChuBaoCao.chuyenThuMuc(id, parentId)),

  /** Chỉ xoá thư mục rỗng; còn con hay tài liệu thì `409 BAO_CAO_FOLDER_NOT_EMPTY`. */
  xoaThuMuc: (id: string): Promise<{ id: string }> =>
    dungApiThat()
      ? goiApi(`${GOC}/folders/${ma(id)}`, { method: 'DELETE' })
      : giaLap(() => mayChuBaoCao.xoaThuMuc(id)),

  // ─── Nguồn ───

  nguon: (): Promise<Source[]> =>
    dungApiThat()
      ? goiApi(`${GOC}/sources`)
      : mockRequest(undefined, [] as Source[]).then((rong) => rong ?? mayChuBaoCao.nguon()),

  taoNguon: (du: CreateSourceInput): Promise<Source> =>
    dungApiThat() ? goiApi(`${GOC}/sources`, { method: 'POST', body: du }) : giaLap(() => mayChuBaoCao.taoNguon(du)),

  suaNguon: (id: string, du: UpdateSourceInput): Promise<Source> =>
    dungApiThat()
      ? goiApi(`${GOC}/sources/${ma(id)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuBaoCao.suaNguon(id, du)),

  datNguonMacDinh: (id: string): Promise<Source> =>
    dungApiThat()
      ? goiApi(`${GOC}/sources/${ma(id)}/set-default`, { method: 'POST' })
      : giaLap(() => mayChuBaoCao.datNguonMacDinh(id)),

  /** `true` gọi `/activate`, `false` gọi `/deactivate`. */
  datTrangThaiNguon: (id: string, bat: boolean): Promise<Source> =>
    dungApiThat()
      ? goiApi(`${GOC}/sources/${ma(id)}/${bat ? 'activate' : 'deactivate'}`, { method: 'POST' })
      : giaLap(() => mayChuBaoCao.datTrangThaiNguon(id, bat)),
};
